import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Phone, ArrowRight, ChevronRight, CheckCircle2, Plus, Minus,
} from "lucide-react";
import { PHONE, PHONE_HREF, SERVICES, SERVICE_LIST } from "@/data/services";
import { useSiteImages } from "@/lib/siteImages";

const Crumb = ({ to, children }) =>
  to ? (
    <Link to={to} className="text-muted hover:text-gold transition-colors">{children}</Link>
  ) : (
    <span className="text-ink">{children}</span>
  );

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left group"
      >
        <span className="font-display text-xl md:text-2xl text-ink group-hover:text-gold transition-colors pr-6">
          {q}
        </span>
        <span className="flex-shrink-0 w-9 h-9 border border-line group-hover:border-gold flex items-center justify-center transition-colors">
          {open ? <Minus size={16} className="text-gold" /> : <Plus size={16} className="text-ink group-hover:text-gold" />}
        </span>
      </button>
      {open && (
        <div className="pb-6 text-muted leading-relaxed max-w-3xl">{a}</div>
      )}
    </div>
  );
};

const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES[slug];
  const { slots } = useSiteImages();

  if (!service) return <Navigate to="/" replace />;

  return (
    <div data-testid={`service-page-${slug}`}>
      {/* Hero */}
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.94) 0%, rgba(5,5,5,0.78) 50%, rgba(5,5,5,0.55) 100%), url(${slots[`service-${slug}`] || service.image})`,
          }}
        />
        <div className="relative mx-auto max-w-page px-6 lg:px-10 pt-20 pb-28">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12 fade-up" aria-label="Breadcrumb">
            <Crumb to="/">Home</Crumb>
            <ChevronRight size={12} className="text-dim" />
            <Crumb to="/#services">Services</Crumb>
            <ChevronRight size={12} className="text-dim" />
            <Crumb>{service.title}</Crumb>
          </nav>
          <div className="max-w-3xl fade-up">
            <div className="eyebrow mb-5">{service.eyebrow}</div>
            <h1 className="font-display text-[clamp(3rem,8vw,7rem)] font-extrabold leading-[0.9] text-ink mb-6">
              {service.title}
            </h1>
            <p className="text-ink text-xl md:text-2xl font-light leading-snug max-w-2xl mb-10">
              {service.hero}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={PHONE_HREF} className="btn-gold" data-testid="service-hero-call">
                <Phone size={16} /> Call {PHONE}
              </a>
              <Link to="/contact" className="btn-ghost" data-testid="service-hero-quote">
                Request a Quote <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 lg:py-28 border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Overview</div>
            <h2 className="font-display text-4xl md:text-5xl text-ink leading-tight">
              The {service.title.toLowerCase()} work most people don't see — done properly.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <p className="text-muted text-lg leading-relaxed">{service.intro}</p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      {service.included && (
        <section className="py-20 lg:py-28 bg-elevated">
          <div className="mx-auto max-w-page px-6 lg:px-10">
            <div className="mb-16 max-w-2xl">
              <div className="eyebrow mb-5">What's Included</div>
              <h2 className="font-display text-4xl md:text-6xl text-ink">Scope of Work</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-line">
              {service.included.map((item, i) => (
                <div
                  key={item.title}
                  data-testid={`service-included-${i}`}
                  className="bg-elevated hover:bg-card transition-colors p-8 group"
                >
                  <CheckCircle2 size={22} className="text-gold mb-5" />
                  <h3 className="font-display text-2xl text-ink mb-3 group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-muted leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process */}
      {service.process && (
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-page px-6 lg:px-10">
            <div className="mb-16 max-w-2xl">
              <div className="eyebrow mb-5">How We Work</div>
              <h2 className="font-display text-4xl md:text-6xl text-ink">Our Process</h2>
            </div>
            <div className="space-y-px bg-line">
              {service.process.map((p, i) => (
                <div
                  key={p.step}
                  data-testid={`service-process-${i}`}
                  className="bg-bg p-8 md:p-10 grid grid-cols-12 gap-6 items-baseline group hover:bg-card transition-colors"
                >
                  <div className="col-span-12 md:col-span-2 font-display text-6xl text-gold/30 group-hover:text-gold transition-colors">
                    {p.step}
                  </div>
                  <h3 className="col-span-12 md:col-span-3 font-display text-3xl text-ink">{p.title}</h3>
                  <p className="col-span-12 md:col-span-7 text-muted leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {service.faqs && (
        <section className="py-20 lg:py-28 bg-elevated">
          <div className="mx-auto max-w-page px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-5">FAQs</div>
              <h2 className="font-display text-4xl md:text-5xl text-ink">Common Questions</h2>
              <p className="text-muted mt-6">
                Don't see your question? Call us on <a href={PHONE_HREF} className="text-gold hover:underline">{PHONE}</a>.
              </p>
            </div>
            <div className="lg:col-span-8">
              {service.faqs.map((f, i) => (
                <FAQItem key={i} idx={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Services */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-page px-6 lg:px-10">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="eyebrow mb-5">More From Styne</div>
              <h2 className="font-display text-4xl md:text-6xl text-ink">Other Services</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-line">
            {SERVICE_LIST.filter((s) => s.slug !== slug).map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="bg-bg hover:bg-card transition-colors p-6 group"
              >
                <h3 className="font-display text-lg text-ink group-hover:text-gold transition-colors mb-2">
                  {s.title}
                </h3>
                <ChevronRight size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-elevated border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-6xl text-ink mb-5">
            Need {service.title}?
          </h2>
          <p className="text-muted text-lg max-w-xl mx-auto mb-10">
            Tell us about your project and we'll provide a detailed quote within 24 hours.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={PHONE_HREF} className="btn-gold" data-testid="service-cta-call">
              <Phone size={16} /> Call Now
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="service-cta-quote">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
